import React, { useEffect, useState, useCallback, useRef, ReactElement } from 'react';

import HeaderContext from './context/HeaderContext';
import HeaderNavBarContext from './context/HeaderNavBarContext';
import HeaderSearchBarContext from './context/HeaderSearchBarContext';

import Box from '../../components/Box/Box';
import HamburgerMenu from './HamburgerMenu';
import NavBar from './Navbar';
import SearchBar from './SearchBar';

import { isSmbFlaggedFromSessionStorageCache } from './utils/fromSessionStorageCache';
import fetchApI from './utils/fetchApi';
import { addClass, removeClass } from './utils/className';
import type { THeaderConfig, TNavBar } from '../../types/THeaderConfig';

// use this heights to avoid cumulative shift layout
const HEADER_NAVBAR_HEIGHT = 57;
const HEADER_HEIGHT_WITH_SEARCHBAR = 122;

type THeader = {
  headerConfig: THeaderConfig;
  children?: ReactElement;
  trackGAEvent?: (eventName: string, eventAction: string) => void;
  className?: string;
};

export function isValidNavBarConfig(navBarConfig: TNavBar): boolean {
  return navBarConfig && Object.entries(navBarConfig).length !== 0;
}

export function getLoggedInState(navBarConfig: TNavBar): boolean {
  return navBarConfig?.userDetails?.isLoggedIn || false;
}

const Header = ({ headerConfig, children, trackGAEvent }: THeader) => {
  const {
    showNavBar = true,
    showSearchBar = false,
    navBar,
    searchBar,
    isSticky = false,
    logoUrls: { logoMobileUrl, logoDesktopUrl },
    ...commonConfig
  } = headerConfig;

  const { searchBarTranslationsForTenant, ...searchBarConfig } = searchBar ?? {};

  const { menuLinks: initialMenuLinks, translations, ...navBarConfig } = navBar ?? ({} as TNavBar);

  let isLoggedIn = false;
  let smbStatusUrl = '';

  if (isValidNavBarConfig(navBarConfig)) {
    isLoggedIn = getLoggedInState(navBarConfig);
    smbStatusUrl = navBarConfig?.smbStatusUrl || '';
  }

  const [menuLinks, setMenuLinks] = useState(initialMenuLinks);
  const [headerHeight, setHeaderHeight] = useState(showSearchBar ? HEADER_HEIGHT_WITH_SEARCHBAR : HEADER_NAVBAR_HEIGHT);
  const [isHamburgerMenuVisible, setHamburgerMenuVisible] = useState(false);
  const [isExpandSearchBar, setExpandSearchBar] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const className = 'hz-no-body-scroll';
    if (isHamburgerMenuVisible) {
      addClass(document.body, className);
    } else {
      removeClass(document.body, className);
    }
  }, [isHamburgerMenuVisible]);

  const headerRef = useCallback((element) => {
    if (element) {
      const setHeight = () => {
        const { height } = element.getBoundingClientRect();
        setHeaderHeight(height);
      };

      if (typeof ResizeObserver === 'function') {
        const resizeObserver = new ResizeObserver((entries) => {
          window.requestAnimationFrame(() => {
            if (!Array.isArray(entries) || !entries.length) {
              return;
            }

            setHeight();
          });
        });
        resizeObserver.observe(element);
      } else {
        setHeight();
      }
    }
  }, []);

  useEffect(() => {
    if (smbStatusUrl && isLoggedIn) {
      const fetchData = async () => {
        const isFlagged = await isSmbFlaggedFromSessionStorageCache(fetchApI, smbStatusUrl);
        if (isFlagged) {
          setMenuLinks((prevState) =>
            prevState?.map(({ show, role, ...rest }) => {
              const newShow = role === 'smbStatus' ? isFlagged : show;
              return { show: newShow, role, ...rest };
            }),
          );
        }
      };

      fetchData();
    }
  }, [smbStatusUrl, isLoggedIn]);

  const showHamburgerMenu = () => setHamburgerMenuVisible(true);
  const hideHamburgerMenu = () => setHamburgerMenuVisible(false);

  const showHamburgerMenuClassName = isHamburgerMenuVisible ? 'hz-Header--showHamburgerMenu' : '';
  const expandSearchBarClassName = isExpandSearchBar ? 'hz-Header--expandSearchBar' : '';
  const stickyHeaderClassName = isSticky ? ' u-stickyHeader' : '';

  return (
    <HeaderContext.Provider value={commonConfig}>
      <HeaderNavBarContext.Provider value={navBarConfig}>
        <header
          ref={ref}
          className={`${showHamburgerMenuClassName}${stickyHeaderClassName}`}
          style={{ height: `${headerHeight}px` }}
        >
          <Box ref={headerRef} as="div" name="Header" className={expandSearchBarClassName}>
            <Box as="div" parent="Header" name="ribbonTop" />
            {showNavBar && menuLinks && (
              <NavBar
                logoMobileSrc={logoMobileUrl}
                logoDesktopSrc={logoDesktopUrl}
                showHamburgerMenu={showHamburgerMenu}
                menuLinks={menuLinks}
                trackGAEvent={trackGAEvent}
                translations={translations}
                wrapHeadLogoH1Tag={navBarConfig?.wrapHeadLogoH1Tag || false}
              />
            )}
            {showSearchBar && searchBarConfig && (
              <HeaderSearchBarContext.Provider value={searchBarConfig}>
                <SearchBar
                  setExpandSearchBar={setExpandSearchBar}
                  trackGAEvent={trackGAEvent}
                  searchBarTranslationsForTenant={searchBarTranslationsForTenant}
                />
              </HeaderSearchBarContext.Provider>
            )}
            {children}
            <Box as="div" parent="Header" name="ribbonBottom" />
          </Box>
          {showNavBar && menuLinks && (
            <HamburgerMenu
              isVisible={isHamburgerMenuVisible}
              menuLinks={menuLinks}
              hideHamburgerMenu={hideHamburgerMenu}
              translations={translations}
            />
          )}
        </header>
      </HeaderNavBarContext.Provider>
    </HeaderContext.Provider>
  );
};

export default Header;
